interface IObjectKeys {
  [key: string]:
    | string
    | number
    | boolean
    | object
    | object[]
    | undefined
    | null;
}

export interface Heartbeat {
  debug: boolean;
  env: string;
  homekeep_version: string;
  python_version: string;
  query_data: object;
}

export interface Health {
  check_id: number;
  debug: boolean;
  delete: number;
  env: string;
  firestore: boolean;
  homekeep_version: string;
  python_version: string;
  read: number;
  write: number;
}

export interface ContactInfo {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  country?: string | null;
  category?: string | null;
  email?: string | null;
  home_ids?: number[];
  home_phone?: string | null;
  id?: number;
  image?: string | null;
  image_id?: string | null;
  label?: string | null;
  mobile_phone?: string | null;
  name?: string;
  postal_code?: string | null;
  state?: string | null;
  type?: string;
  type_id?: number;
  url?: string | null;
  work_phone?: string | null;
}

export interface AppointmentProposalGroup {
  date: string;
  proposals: AppointmentProposal[];
}

export interface AppointmentProposal {
  duration: number;
  schedule_hash: string;
  schedule_start: string;
  schedule_end: string;
  time: string;
  day: string;
  date: string;
}

export interface Appointment extends IObjectKeys {
  allow_cancel: boolean;
  allow_reschedule: boolean;
  assets: Asset[];
  created: string;
  description: string;
  duration: number;
  google_event_id: number;
  home_id: number;
  id: number;
  image?: string;
  finished?: string;
  name: string;
  homeowner_notes?: string;
  scheduled: string;
  worker?: Worker;
  worker_notes?: string;
  type?: string;
  type_id?: number;
  // UI convenience properties
  display_date: string;
  display_time: string;
}

export interface Worker {
  id?: number;
  type?: string;
  type_id?: number;
  name?: string;
  title?: string;
  image?: string;
  rating?: number;
  google_calendar_id?: number;
}

export interface Task extends IObjectKeys {
  assets: Asset[];
  finished: string;
  id: number;
  image?: string;
  name: string;
  worker_notes?: string;
  statuses?: StatusTag[];
  tag_ids?: string;
  type?: string;
  type_id?: number;
}

export interface TaskBodyDetails {
  finished?: Date;
  finished_by_id?: number | null;
  tag_ids?: number[];
  notes?: any;
}

export interface StatusTag {
  digital: boolean;
  id: number;
  level: number;
  name: string;
}

export interface Document {
  created: string;
  id: number;
  name: string;
  image: string;
  file_url: string;
  type: string;
  thumbnail_url: string | null;
  type_id: number;
  product_data: {
    manufacturer: number;
    manufacturer_name: string;
    manufacturer_image: string;
    model: string;
    name: string;
    scope: string;
    serial: string;
    type_name: string;
  };
}

export interface Attribute {
  name: string;
  value: string;
}

export interface InventoryItem {
  home_id: number;
  assets: Asset[];
  name: string;
  id: number;
  image: string;
  hex_value?: string | null;
  model: string;
  registered: string;
  product_template: ProductTemplate;
  documents: Document[];
  serial: string;
  task_groups: object[];
  type: string;
  type_id: number;
  attributes?: InventoryItemAttributes;
}

// Per-type attribute set from `Products.attributes` / `type_attributes` (see
// tickets/RHC10-15411/contract.md). Keys are lowercased by the backend's
// `StandardBase._clean_field` (e.g. `optionCategory` -> `optioncategory`)
// regardless of the MarkSystems source payload's casing — confirmed against
// a live `GET /home/<id>/product_group/67` response, which returns
// `optioncategory`/`optioncode`/`salesprice`, not camelCase. Group 67 (Home
// Selections / options) carries optioncategory/location/optioncode/
// salesprice/quantity; group 68 (Home Colors) carries colorgroupcode/
// colorgroupdesc/itemcode/itemdesc/colordesc. A group other than 67/68
// carries a different key set entirely. Also present on `SearchedProduct` —
// `SharedProductListSchema` (the product search endpoint's schema) exposes
// the same `type_attributes`/`attributes` field as the product-group list.
export interface InventoryItemAttributes {
  optioncategory?: string;
  location?: string;
  optioncode?: string;
  description?: string;
  salesprice?: number;
  quantity?: number;
  colorgroupcode?: string;
  colorgroupdesc?: string;
  itemcode?: string;
  itemdesc?: string;
  colordesc?: string;
}

export interface AttributeSchema {
  allow?: any;
  default: string;
  field: string;
  label: string;
  type: string;
}

export interface ProductTemplate {
  assets: Asset[];
  created: string;
  description: string;
  id: number;
  image: string;
  image_id: number;
  manufacturer: Manufacturer;
  manufacturer_image: string;
  manufacturer_name: string;
  model: string;
  name: string;
  rooms: Room[];
  type: string;
  type_data?: any;
  type_id: number;
}

export interface ProductTemplateType {
  attribute_schema: AttributeSchema[];
  field: string;
  id: number;
  label: string;
  product_attribute_schema: AttributeSchema[];
  type_group_name: string;
}

export interface ProductBodyDetails {
  home_id?: number;
  type?: string;
  model_photo?: string;
  brand_photo?: string;
  attributes?: any;
  /** Maps directly to `Products.serial` — not a dynamic `attributes` key. */
  serial?: string;
  /** A specific catalog Model # picked via ProductModelSearchSelect. Wins over manufacturer_id if both are present. */
  product_template_id?: number;
  /** A Brand/Manufacturer picked with no specific Model # — API resolves this to a found-or-created "Default/Generic" template for that brand+type. */
  manufacturer_id?: number;
}

export interface ProductGroupType {
  id: number;
  name: string;
  product_template_types: ProductTemplateType[];
}

/**
 * One row from the homeowner-safe `GET /data/product_models` catalog search.
 *
 * Resolve a picked model to a Product Type / Group / Brand by **id**
 * (`type_id`, `manufacturer_id`) — never by matching `type`/`manufacturer`
 * against local labels. Those strings are admin-editable display text; a
 * rename in the admin catalog would silently break label matching, while ids
 * are stable.
 */
export interface ProductModelSearchResult {
  id: number;
  name: string;
  model: string;
  manufacturer: string;
  /** `manufacturers.id` — set a Brand select directly from this. */
  manufacturer_id: number;
  type: string;
  /**
   * `product_template_types.id` — set a Product Type select from this, and
   * derive the Group from whichever `ProductGroupType` contains that type id.
   */
  type_id: number;
}

/** One row from the homeowner-safe `GET /home/{home_id}/manufacturers` catalog browse. */
export interface ManufacturerSearchResult {
  id: number;
  name: string;
}

/**
 * Body for `PATCH /home/{home_id}/product/{id}` — the only fields a homeowner
 * may edit on their own product instance. Group, Product Type, and the
 * catalog brand/model text are immutable here; re-pointing `product_template_id`
 * is how a homeowner "changes the model".
 */
export interface UpdateProductBody {
  serial?: string | null;
  registered?: string | null;
  product_template_id?: number;
  // Either accepted by PATCH /home/{home_id}/product/{id} — the API's
  // _manage_photo() creates a new Assets row (type_id=3) from the URL and
  // appends it to the product's `assets` collection either way; there is no
  // functional difference between the two field names on the backend.
  model_photo?: string;
  brand_photo?: string;
  // Brand/Manufacturer picked with no specific Model #. Ignored by the API if
  // product_template_id is also present. Server finds-or-creates a
  // product_templates row for (manufacturer_id, the product's current
  // type_id) with model literally "Default/Generic" and repoints the product
  // at it.
  manufacturer_id?: number;
  // Per-instance display name (e.g. Color Selections' "Location") — a plain
  // column on every StandardObject-based model (BaseUpdateSchema.name), not
  // catalog-derived, so it's safe to PATCH directly.
  name?: string;
  // Merged per-key into the product's JSONB `attributes` column by
  // Products.update_extra — NOT a blind overwrite — and gated server-side to
  // only the keys declared in that product's own type_attribute_schema (e.g.
  // `hex_value` for marksystems_colors products only). Safe to send just the
  // key(s) being changed.
  attributes?: { hex_value?: string | null };
}

export interface SearchedProduct {
  group_id: number;
  group_name: string;
  id: number;
  image: string;
  image_id: number;
  name: string;
  product_template: ProductTemplate;
  registered: string;
  serial: string;
  type: string;
  type_id: number;
  typed: number;
  attributes?: InventoryItemAttributes;
}

export interface Manufacturer {
  id: number;
  image: string;
  name: string;
  type: string;
  type_id: number;
}

export interface Room {
  id: number;
  image: string;
  name: string;
  type: string;
  type_id: number;
}

export interface InventoryGroup {
  id: number;
  items: InventoryItem[];
  label: string;
}

export interface Asset extends IObjectKeys {
  file_url: string;
  id: number;
  name: string;
  type: string;
  type_id: number;
  cloud_store: string | null;
  default: boolean;
  file_name: string | null;
  opt_home_id: number | null;
  thumb_cloud_store: string | null;
}

export interface AssetGroup {
  id: number;
  items: Asset[];
  label: string;
}

export enum HKSignupStep {
  Zip = 1,
  Email,
  Size,
  Packages,
  Done
}

export enum HKHouseSize {
  Small = 1,
  Medium,
  Large
}

export enum HKMarketTier {
  High = 1,
  Medium,
  Low
}

export enum HKPackageType {
  Essential = 'es',
  Preferred = 'pr'
}

export interface HKPackage {
  type: HKPackageType;
  name: string;
  price: string;
  term: string;
  code: string;
}

export interface HKSubscription {
  code: string;
  name: string;
}

export interface DigitalSignup {
  access_token: string;
  home_id: number;
  user_id: number;
}

export interface DigitalTask {
  duration: number | null;
  finished: boolean | null;
  id: number;
  image: string | null;
  image_id: number | null;
  name: string;
  notes: string | null;
  product_type_id: number | null;
  product_type_label: string | null;
  product_type_name: string | null;
  scheduled: string;
  tutorial_url: string | null;
  type: string;
  type_id: number;
  uncompletable?: boolean;
}

export interface DigitalTaskDetails {
  consumables: DigitalConsumable[];
  diy_instructions: string;
  duration: number;
  finished: string | null;
  finished_by: string | null;
  id: number;
  image: string | null;
  image_id: number | null;
  name: string;
  notes: string | null;
  product: DigitalProduct | null;
  product_type_id: number | null;
  product_type_name: string;
  scheduled: string;
  sequence: number;
  status_tags: StatusTag[];
  steps: any[];
  tools: DigitalConsumable[];
  tutorial_url: string | null;
  type: string;
  type_id: number;
  why: string;
}

export interface DigitalConsumable {
  link: string | null;
  name: string;
}

export interface DigitalProduct {
  id: number;
  image: string | null;
  image_id: number | null;
  is_generic: boolean;
  name: string;
  registered: string;
  type: string;
  type_id: number;
}

export interface DigitalProductTypeTogglesList {
  toggles: DigitalProductTypeToggle[];
}

export interface DigitalProductTypeToggle {
  current_state: boolean;
  group_id: number;
  group_name: string;
  id: number;
  label: string;
}

export interface DigitalCatalogTask {
  description: string;
  duration: number | null;
  finished: string | null;
  id: number;
  image: string | null;
  image_id: number | null;
  name: string;
  product_type_group: string;
  product_type_group_id: number;
  product_type_id: number;
  product_type_name: string;
  scheduled: string;
  tutorial_url: string | null;
  type: string;
  type_id: number;
}

export interface DocumentCount {
  count: number;
  type: string;
  group: string;
  label: string;
}

export interface DocumentType {
  attribute_schema: any[];
  field: string;
  id: number;
  label: string;
}

export interface DocumentBody {
  name: string;
  type: string | null;
  file_url: string;
}

export interface SearchedDocument {
  file_url: string;
  id: number;
  image_id: number | null;
  name: string;
  opt_home_id: number;
  optimized_url: string | null;
  thumbnail_url: string | null;
  type: string;
  type_id: number;
  type_label: string;
}

export interface BuilderLogo {
  name: string;
  logo_url: string;
}
